import './App.scss';

import Debug from 'debug';
import React, { useEffect, useState, useCallback, useReducer } from 'react';

import { LoadingDots } from '@bblabs/module-library/atoms';
import { UserConfigProvider } from '@bblabs/module-library/classes';
import { Environment } from '../../types/config';
import { canSkipTokenForFiltering, getMailbox } from '../abstractions/office';
import { OverlayCoordinatorContext, useOverlayCoordinator } from '../coordinators/overlay';
import { environment } from '../../environment';
import { Token } from '../models/Token';
import { User } from '../models/User';
import { MetricService } from '../services/MetricService';
import { userService } from '../services/UserService';
import { isViewInComposeMode } from '../abstractions/office';
import { useUserState } from '../hooks/useUserState';
import { useLivefireApi } from '../hooks/useLivefireApi';
import { VideoMetadata } from '../lib/OutlookLivefireApi';
import { eventService } from '../services/EventService';
import { prependHtmlToEmail } from '../services/bixel/email';
import { getSentAndInboxFolders } from '../abstractions/Outlook';
import { waitForErrorModal } from '../lib/wait';
import { Login } from './Login';
import { Tabs } from './Tabs/Tabs';
import { Tracking } from './Tracking';
import { VideoLibrary } from './VideoLibrary';
import { AccountOverlay } from './AccountOverlay';
import { TrialBanner } from './TrialBanner';
import { Record } from './Record';
import { EmailSent } from './EmailSent';
import { TrackingControls } from './TrackingControls';
import { Settings } from './Settings';

const debug = Debug('o365:App');

export type UserStatus = {
  loggedin: AppLoginState;
  user: User | null;
  token: Token | null;
  inTrial: boolean;
  daysLeftInTrial: number;
  buyNowUrl: string | null;
  subscriptionStatus: 'CLOSED' | 'EXPIRED' | 'ACTIVE' | 'PENDING' | null;
  isVideoReviewEnabled: boolean | undefined;
};

/**
 * State of the login
 *
 * - true: logged in
 * - false: logged out
 * - pending: user has initiated login flow
 * - loading: not-user-initiated login flow happening...
 */
export type AppLoginState = boolean | 'pending' | 'loading';

/**
 * Are we in an office context?
 */
export type IsOfficeState = Exclude<AppLoginState, 'pending'>;
export type TabType = 'tracking' | 'video-library' | 'record' | null;

export type OfficeInfo = {
  host: Office.HostType;
  platform: Office.PlatformType;
};

export type View = 'app' | 'settings' | 'login';

/**
 * Determine if we need to show the sent message for users
 */
export type ShowSentMessage = boolean | 'none';

export const EnvironmentContext = React.createContext<Environment>(environment);

export const defaultUserContext: UserStatus = {
  user: null,
  token: null,
  loggedin: false,
  inTrial: false,
  daysLeftInTrial: -1,
  buyNowUrl: null,
  subscriptionStatus: null,
  isVideoReviewEnabled: false,
};
export const UserContext = React.createContext<UserStatus>(defaultUserContext);

type AppState = {
  isLoading: boolean;
  isOffice: IsOfficeState;
};

type AppStateAction = { type: 'change'; isOffice?: IsOfficeState; isLoading?: boolean };

function appStateReducer(state: AppState, action: AppStateAction) {
  const newState = { ...state };
  if ('isOffice' in action && typeof action.isOffice !== 'undefined') {
    newState.isOffice = action.isOffice;
  }
  if ('isLoading' in action && typeof action.isLoading !== 'undefined') {
    newState.isLoading = action.isLoading;
  }
  return newState;
}

/**
 * The parent of all the things...
 */
export const App = () => {
  const [state, dispatcher] = useReducer(appStateReducer, { isLoading: true, isOffice: 'loading' });
  const [userState, setUserState] = useState<UserStatus>(defaultUserContext);
  const [loginMessage, setLoginMessage] = useState('');
  const [activeTab, setActiveTab] = useState<TabType>('tracking');
  const [overlayState, overlayDispatch] = useOverlayCoordinator();
  const [showSent, setShowSent] = useState<ShowSentMessage>('none');
  const [officeInfo, setOfficeInfo] = useState<OfficeInfo | null>(null);
  const [view, setView] = useState<View>('app');

  /**
   * Listen for outlook item changes
   *
   * This method attaches to the ItemChanged office event. We don't need to do anything
   * in this, we just have to attach to the event to prevent other errors from happening.
   * It is fired when we are pinned in both read mode and compose mode.
   */
  const itemChangedListener = useCallback(
    (...args) => {
      debug('itemChanged', args);
      if (showSent === true) {
        debug('setShowSent', false);
        setShowSent(false);
      } else if (isViewInComposeMode()) {
        setActiveTab('record');
      }
    },
    [showSent],
  );

  // Outlook can really only work within Office, so figure out if
  // this is loaded in outlook or not
  useEffect(() => {
    async function onInsertVideo(video: VideoMetadata) {
      MetricService.yardstick('events.insert_video_receive.success');
      setActiveTab('video-library');
      debug('onInsertVideo', video);
      const thumbnailUrl = video.true_thumbnail_url;
      const thumbnail = document.createElement('img');
      thumbnail.id = video.id;
      thumbnail.src = thumbnailUrl;
      thumbnail.style.maxWidth = '100%';

      const link = document.createElement('a');
      link.href = video.full_url;
      link.target = '_blank';
      link.appendChild(thumbnail);

      try {
        await prependHtmlToEmail(link.outerHTML);
        MetricService.yardstick('email.video_insert.success');
        overlayDispatch('videoInsert');
        eventService.emit('videoInserted');
      } catch (err) {
        console.error(err);
      }
    }

    async function onVideoReviewVideoSaved() {
      setActiveTab('video-library');
      try {
        await waitForErrorModal(
          'VIDEO_UNDER_REVIEW',
          () => {
            /* intentional, only has close button */
          },
          { displayInIframe: true, width: 25, height: 35 },
        );
      } catch (error) {
        console.error('Error opening modal', error);
      }
    }

    eventService.addListener('insertVideo', onInsertVideo);
    eventService.addListener('videoSaved', onVideoReviewVideoSaved);
    try {
      Office.onReady((info) => {
        // REST APIs are deprecated. Avoiding them when possible.
        if (!canSkipTokenForFiltering()) {
          getSentAndInboxFolders();
        }
        debug('isOffice', !!(info.host && info.platform));
        isViewInComposeMode() ? setActiveTab('record') : setActiveTab('tracking');
        dispatcher({
          type: 'change',
          isOffice: !!(info.host && info.platform),
          isLoading: false,
        });
        debug('set isLoading=false');
        setOfficeInfo(info);

        getMailbox().addHandlerAsync(Office.EventType.ItemChanged, itemChangedListener);
      });
    } catch (err) {
      debug('set isOffice=false,isLoading=false');
      dispatcher({
        type: 'change',
        isOffice: false,
        isLoading: false,
      });
    }

    return () => {
      eventService.removeListener('insertVideo', onInsertVideo);
      eventService.removeListener('videoReviewVideoSaved', onVideoReviewVideoSaved);
    };
  }, [overlayDispatch, itemChangedListener]);

  useEffect(() => {
    if (officeInfo === null) {
      return;
    }
    if (officeInfo.platform === Office.PlatformType.PC && showSent === 'none') {
      debug('show sent for this environment is ON');
      setShowSent(false);
    }
  }, [officeInfo, showSent]);

  useUserState({
    isOffice: state.isOffice,
    setLoginMessage,
    setUserState,
    userState,
  });

  useEffect(() => {
    if (userState.loggedin === false || userState.loggedin === 'pending') {
      setView('login');
    } else if (view === 'login') {
      setView('app');
    }
  }, [userState, view]);

  useLivefireApi({ userState });

  /**
   * Trigger the Email Sent view when S&T is used
   */
  const onEmailSend = useCallback(() => {
    if (showSent !== 'none') {
      debug('setShowSent', true);
      setShowSent(true);
    }
  }, [showSent]);

  const tabSelected = (tab: TabType) => {
    if (tab) {
      setView('app');
      MetricService.increment(`click.task_pane.${tab.replace(/-/g, '_')}.tab`);
    }
    setActiveTab(tab);
  };

  const settingsSelected = () => {
    MetricService.increment('click.task_pane.settings');
    setView('settings');
  };

  // show loading dots if:
  //   - we're still loading the add-in
  //   - we're loading login data from settings
  if (state.isLoading || state.isOffice === 'loading' || userState.loggedin === 'loading') {
    return (
      <div className="loading">
        <LoadingDots />
      </div>
    );
  }
  debug('App: done loading', userState.loggedin);

  // show a message if the user is not currently in outlook
  if (state.isOffice === false) {
    return (
      <div className="loading">
        <p>Please open in Outlook!</p>
      </div>
    );
  }

  // at this point, we have a loaded config & we're in office...
  return (
    <EnvironmentContext.Provider value={environment}>
      <UserContext.Provider value={userState}>
        <OverlayCoordinatorContext.Provider value={overlayDispatch}>
          <div className="app">
            {view === 'login' && (
              <Login
                state={userState.loggedin}
                message={loginMessage}
                onLoginClick={userService.onLoginClick}
                onLoginCodeClick={userService.onLoginCodeClick.bind(userService)}
                onSignupClick={userService.onSignupClick}
              />
            )}
            {view !== 'login' && userState.token && (
              <div className={`app-container view--${view}`}>
                {showSent === true ? (
                  <EmailSent />
                ) : (
                  <UserConfigProvider
                    token={userState.token.getAccessToken() ?? ''}
                    environment={environment}
                  >
                    <TrialBanner />
                    <AccountOverlay />
                    <div className="app-container__tabs">
                      <Tabs onSelect={tabSelected} activeTab={view === 'app' ? activeTab : null} />
                      <TrackingControls
                        onSend={onEmailSend}
                        showSendInfoOverlay={overlayState.showInfoOverlay}
                      />
                    </div>
                    <div className="app-container__main">
                      {view === 'app' && activeTab === 'tracking' && <Tracking />}
                      {view === 'app' && activeTab === 'video-library' && <VideoLibrary />}
                      {view === 'app' && activeTab === 'record' && <Record />}
                      {view === 'settings' && <Settings />}
                    </div>
                  </UserConfigProvider>
                )}
                <div className="settings-button-container">
                  <button onClick={settingsSelected} className="settings">
                    Settings
                  </button>
                </div>
              </div>
            )}
          </div>
        </OverlayCoordinatorContext.Provider>
      </UserContext.Provider>
    </EnvironmentContext.Provider>
  );
};
